import * as React from 'react';

import {
  isAuthenticationResponse,
  isEmailVerificationRequiredError,
  isMfaChallengeError,
  isMfaEnrollmentRequiredError,
  isOrganizationSelectionError,
  isInvalidCredentialsError,
} from '@rematter/frontend-lib-api-client';
import { Button, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';

import { useSignIn } from '../hooks/mutations/useSignIn';
import { $signIn, OrganizationRecord } from '../state/signIn.state';

import { useTranslations } from '~/app/i18n/useTranslations';
import { config } from '~/config';
import { useIdentity } from '~/shared/hooks';

export const SignInCredentials = () => {
  const toast = useToast();
  const t = useTranslations();

  const [signInState, setSignInState] = useAtom($signIn);
  const [email, setEmail] = React.useState(signInState.email || '');
  const [password, setPassword] = React.useState('');

  const { setAccessToken, setUser, setOrganization } = useIdentity();

  const { mutateAsync: signIn, isPending } = useSignIn();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { authenticationSignIn } = await signIn({
        clientId: config.workOs.clientId ?? '',
        email,
        password,
      });

      if (isAuthenticationResponse(authenticationSignIn)) {
        setSignInState({ ...signInState, authenticated: true });
        setAccessToken(authenticationSignIn.accessToken);
        setUser(authenticationSignIn.user);
        setOrganization(authenticationSignIn.organization);
        return;
      }

      if (isEmailVerificationRequiredError(authenticationSignIn)) {
        setSignInState({
          ...signInState,
          step: 'email-verification',
          email,
          pendingAuthenticationToken:
            authenticationSignIn.pendingAuthenticationToken,
        });
        return;
      }

      if (isMfaEnrollmentRequiredError(authenticationSignIn)) {
        setSignInState({
          ...signInState,
          step: 'mfa-enroll',
          email,
          pendingAuthenticationToken:
            authenticationSignIn.pendingAuthenticationToken,
        });
        return;
      }

      if (isMfaChallengeError(authenticationSignIn)) {
        setSignInState({
          ...signInState,
          step: 'mfa-challenge',
          email,
          pendingAuthenticationToken:
            authenticationSignIn.pendingAuthenticationToken,
          mfa: {
            challengeId:
              authenticationSignIn.authenticationFactors[0].totp?.qrCode,
          },
        });
        return;
      }

      if (isOrganizationSelectionError(authenticationSignIn)) {
        setSignInState({
          ...signInState,
          step: 'org-selection',
          email,
          organizations:
            authenticationSignIn.organizations as OrganizationRecord[],
          pendingAuthenticationToken:
            authenticationSignIn.pendingAuthenticationToken,
        });
        return;
      }

      if (isInvalidCredentialsError(authenticationSignIn)) {
        toast.error(t.signIn.errors.invalid);
      }
    } catch (err) {
      toast.error(String(err));
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
  };

  return (
    <form
      onSubmit={handleLogin}
      className="@SignInCredentials flex flex-col gap-5"
    >
      <TextField
        autoFocus
        label={t.signIn.emailField.label}
        placeholder={t.signIn.emailField.placeholder}
        type="email"
        onChange={handleEmailChange}
        value={email}
        isRequired
      />

      <TextField
        label={t.signIn.passwordField.label}
        placeholder={t.signIn.passwordField.placeholder}
        type="password"
        onChange={handlePasswordChange}
        value={password}
        isRequired
        className="mb-4"
      />

      <Button
        type="submit"
        variant="solid"
        color="primary"
        isLoading={isPending}
      >
        {t.signIn.submit}
      </Button>

      <div className="relative my-4 flex items-center gap-3">
        <div className="grow border-t border-neutral/20"></div>
        <span className="text-neutral">{t.signIn.or}</span>
        <div className="grow border-t border-neutral/20"></div>
      </div>

      <div className="text-center text-sm text-neutral">
        {t.signIn.noAccount}{' '}
        <a
          href="/sign-up"
          className="text-primary-darker hover:text-primary/80"
        >
          Sign up
        </a>
      </div>
    </form>
  );
};
